import { Icon } from '@iconify/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Endpoint } from '@/Config/api/endpoint';
import { httpClient } from '@/Config/api/httpClient.config';
import { QueryKey } from '@/Config/api/queryKey';
import { KnowledgeBase } from '@/Domain/api/KnowledgeBase/dto/KnowledgeBase.dto';
import { AccountDto } from '@/Domain/api/User/dto/Account.dto';
import { PromptTemplateDropdown } from '@/Domain/components/KnowledgeBase/PromptTemplateDropdown';
import { AiCommandsList } from '@/Domain/components/Shared/AiCommandsList/AiCommandsList';
import { BoxIcon } from '@/Domain/components/Shared/BoxIcon/BoxIcon';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@/Domain/components/Shared/Field/Field';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { AutosizeTextarea } from '@/Domain/components/Shared/Textarea/Textarea';
import { useSubscription } from '@/Domain/components/SubscriptionProvider';
import { PrivilegesEnum } from '@/Domain/enum/Privileges.enum';
import { useUpdateAssistantList } from '@/Domain/hooks/AI/useAssistantList.hook';
import { useAutoSubmit } from '@/Domain/hooks/App/useAutoSave.hook';
import { maxWords, characterLimit } from '@/Domain/utils/validate';

type Props = {
  knowledgeBaseData: KnowledgeBase;
};

// TODO: change name; change bootstrap to tailwind; create custom hooks to fetch data
export const ProfilePage = ({ knowledgeBaseData }: Props) => {
  const { t } = useTranslation(['knowledge_base', 'validation']);
  const queryClient = useQueryClient();

  const { hash } = useLocation();
  const { scenario } = useParams();

  const { updateAssistant } = useUpdateAssistantList();
  const { isActivated, isFetched } = useSubscription();
  const aiGuidelinesLocked = isFetched && !isActivated('ai_guidelines');
  const account = queryClient.getQueryData<AccountDto>(['account']);

  const form = useForm<KnowledgeBase>({
    defaultValues: {
      one_word_description: knowledgeBaseData.one_word_description || '',
      short_description: knowledgeBaseData.short_description || '',
      ai_commands: knowledgeBaseData.ai_commands || [],
    },
    shouldFocusError: false,
  });

  const { control } = form;

  const mutation = useMutation({
    mutationKey: ['knowledge_base'],
    mutationFn: async (updatedData: Partial<KnowledgeBase>) => {
      queryClient.setQueryData(['knowledge_base'], () => ({
        ...knowledgeBaseData,
        ...updatedData,
      }));

      const response = await httpClient.patch('/v1/api/knowledge_base/', updatedData);
      return response.data;
    },
    onSuccess: (data: KnowledgeBase) => {
      queryClient.setQueryData(['knowledge_base'], data);
      updateAssistant(scenario!, { name: data.one_word_description });
      void queryClient.invalidateQueries({ queryKey: [Endpoint.ai.assistant] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.onboarding.progress] });
    },
    onError: (_, prevData) => {
      queryClient.setQueryData(['knowledge_base'], () => prevData);
    },
  });

  const onSubmit = (data: KnowledgeBase): void => {
    const cleanedData: Partial<KnowledgeBase> = Object.entries(data).reduce(
      (acc, [key]) => ({ ...acc, [key as keyof KnowledgeBase]: data[key as keyof KnowledgeBase] }),
      {},
    );

    if (Object.keys(cleanedData).length > 0) {
      mutation.mutate(cleanedData);
    }
  };

  useAutoSubmit(form, (data) => onSubmit(data));

  return (
    <form autoComplete="off" data-lpignore="true" data-form-type="other">
      <div className="card border-0">
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="one_word_description">{t('form.name.label')}</FieldLabel>
            <FieldDescription>{t('form.name.help')}</FieldDescription>
            <Controller
              name="one_word_description"
              control={control}
              rules={{
                required: t('required', { ns: 'validation' }),
                maxLength: 40,
              }}
              render={({ field, fieldState: { invalid, error } }) => (
                <>
                  <Input
                    type="text"
                    size="sm"
                    maxLength={40}
                    autoCorrect="off"
                    id="one_word_description"
                    placeholder={t('form.name.placeholder')}
                    autoFocus={hash === `#${field.name}`}
                    {...field}
                    error={invalid}
                    autoComplete="off"
                    data-lpignore="true"
                    data-form-type="other"
                  />
                  <FieldError>{error?.message}</FieldError>
                </>
              )}
            />
          </Field>

          <Field>
            <FieldLabel htmlFor="short_description">{t('form.description.label')}</FieldLabel>
            <FieldDescription>{t('form.description.help')}</FieldDescription>
            <Controller
              name="short_description"
              control={control}
              rules={{
                required: t('required', { ns: 'validation' }),
                maxLength: 15000,
              }}
              render={({ field, fieldState: { error } }) => (
                <>
                  <div className="relative">
                    <AutosizeTextarea
                      {...field}
                      className="px-4 pt-3 pb-12 rounded-xl"
                      maxLength={15000}
                      id="short_description"
                      placeholder={t('form.description.placeholder')}
                      minHeight={300}
                      maxHeight={700}
                      autoComplete="off"
                      data-lpignore="true"
                      data-form-type="other"
                      aria-describedby="short_description_help"
                      value={field.value}
                      autoFocus={hash === `#${field.name}`}
                    />
                    <PromptTemplateDropdown
                      hasExistingValue={(field.value || '').trim().length > 0}
                      onApply={(text) =>
                        form.setValue('short_description', text, {
                          shouldDirty: true,
                          shouldValidate: true,
                        })
                      }
                      triggerClassName="absolute bottom-2 left-2"
                      dropdownAlign="start"
                      data-testid="identity-profile-templates"
                    />
                  </div>
                  <FieldError>{error?.message}</FieldError>
                </>
              )}
            />
          </Field>

          {account?.privileges.includes(PrivilegesEnum.AI_GUIDELINES) && (
            <Field>
              <FieldLabel>{t('form.commands.label')}</FieldLabel>
              <FieldDescription>{t('form.commands.help')}</FieldDescription>
              <div className="relative">
                <Controller
                  name="ai_commands"
                  control={control}
                  rules={{
                    validate: {
                      maxWords: async (value: KnowledgeBase['ai_commands'] = []) =>
                        await (maxWords(value.join(' '), 1000) ||
                          t('max_words', {
                            max: 1000,
                            ns: 'validation',
                          })),
                      characterLimit: async (value: KnowledgeBase['ai_commands'] = []) =>
                        await (characterLimit(value.join(''), 5000) ||
                          t('character_limit', {
                            max: 5000,
                            ns: 'validation',
                          })),
                    },
                  }}
                  render={({ field: { onChange, value = [] }, fieldState: { invalid, error } }) => (
                    <>
                      <AiCommandsList
                        value={value}
                        onChange={onChange}
                        disabled={aiGuidelinesLocked}
                        error={invalid}
                      />
                      <FieldError>{error?.message}</FieldError>
                      {aiGuidelinesLocked && (
                        <div className="group absolute left-0 top-0 flex h-full w-full flex-col items-center justify-center gap-2 rounded-xl transition-all duration-300 hover:bg-accent/60 hover:backdrop-blur-sm">
                          <BoxIcon variant="outlined" className="mx-auto group-hover:block hidden">
                            <Icon
                              icon="mingcute:calendar-time-add-line"
                              className="mx-auto h-full text-muted-foreground"
                            />
                          </BoxIcon>
                          <Trans
                            i18nKey="common:upgrade_package"
                            t={t}
                            components={{
                              upgrade: (
                                <Link
                                  to="../subscription"
                                  className="hidden text-xs font-medium text-foreground group-hover:block"
                                />
                              ),
                            }}
                          />
                        </div>
                      )}
                    </>
                  )}
                />
              </div>
            </Field>
          )}
        </FieldGroup>
      </div>
    </form>
  );
};
